import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { useRegisterMutation } from '../slices/usersApiSlice';
import { useSendBeaumontCodeMutation, useConfirmBeaumontCodeMutation } from '../slices/usersApiSlice';
import { toast } from 'react-toastify';
import { useGetHospitalLocationsQuery } from '../slices/hospitalLocationApiSlice.js';
import { logout } from '../slices/authSlice';
import { CATEGORY_OF_STAFF } from '../utils/enums';

import { apiSlice } from '../slices/apiSlice'; 


const dropdownStyle = { backgroundColor: '#fff' };
const textStyle     = { backgroundColor: '#fff' };

// ─── Shared card / section styling ─────────────────────────────────────────
// Boxed, gray section labels — same visual language as the admin header's
// dropdown section headers, for consistency across the app.
const cardStyle = {
  border: '1px solid #b6d4f0',
  borderRadius: 12,
  padding: 22,
  backgroundColor: '#e3f2fd',
  boxShadow: '0 1px 4px rgba(16, 24, 40, 0.05)',
  height: '100%',
};

const sectionLabelStyle = {
  display: 'inline-block',
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: '#20303e',
  backgroundColor: '#dde3e8',
  borderRadius: 6,
  marginBottom: 18,
};

const SectionLabel = ({ children }) => <div style={sectionLabelStyle}>{children}</div>;



// ─── OTP digit input (same pattern as LoginScreen) ───────────────────────────
const OTPInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([]);
  const digits    = value.split('');

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = '';
      } else if (index > 0) {
        next[index - 1] = '';
        inputRefs.current[index - 1]?.focus();
      }
      onChange(next.join(''));
    }
  };

  const handleChange = (index, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = char;
    onChange(next.join(''));
    if (char && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '12px 0' }}>
      {Array.from({ length: 6 }, (_, i) => (
        <input
          key={i}
          ref={(el) => (inputRefs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          disabled={disabled}
          style={{
            width: 44,
            height: 52,
            border: `2px solid ${digits[i] ? '#1976d2' : '#ccc'}`,
            borderRadius: 8,
            fontSize: '1.6rem',
            fontWeight: 700,
            textAlign: 'center',
            fontFamily: 'monospace',
            backgroundColor: digits[i] ? '#e3f2fd' : '#fff',
            color: '#1976d2',
            outline: 'none',
          }}
        />
      ))}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const RegisterScreen = () => {
  // ── Beaumont verification state ───────────────────────────────────────────
  const [beaumontEmail,     setBeaumontEmail]     = useState('');
  const [beaumontOtp,       setBeaumontOtp]       = useState('');
  const [beaumontVerified,  setBeaumontVerified]  = useState(false);
  const [codeSent,          setCodeSent]          = useState(false);
  const [resendCooldown,    setResendCooldown]    = useState(0);

  const [sendBeaumontCode,    { isLoading: sendingCode }]    = useSendBeaumontCodeMutation();
  const [confirmBeaumontCode, { isLoading: confirmingCode }] = useConfirmBeaumontCodeMutation();

  // ── Registration form state ───────────────────────────────────────────────
  const [name,            setName]            = useState('-');
  const [email,           setEmail]           = useState('');
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstname,       setFirstname]       = useState('');
  const [lastname,        setLastname]        = useState('');
  const [employeenumber,  setEmployeenumber]  = useState('');
  const [jobtitle,        setJobtitle]        = useState('');
  const [salutation,      setSalutation]      = useState('--');
  const [comment,         setComment]         = useState('--');

  //const [effectiveDate,   setEffectiveDate]   = useState(new Date());
  //const [reviewDate,      setReviewDate]      = useState(new Date());
  //default to today and 1 year from today as ISO date strings
const today    = new Date().toISOString().slice(0, 10);
const nextYear = new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                   .toISOString().slice(0, 10);

const [effectiveDate, setEffectiveDate] = useState(today);
const [reviewDate,    setReviewDate]    = useState(nextYear);


  const [openingBalance,  setOpeningBalance]  = useState(0);
  const [bringForward,    setBringForward]    = useState(0);


  const [speciality,            setSpeciality]            = useState('');
  const [specialityID,          setSpecialityID]          = useState('');
  const [directoratedepartment, setDirectoratedepartment] = useState('');
  const [directorateID,         setDirectorateID]         = useState('');

  // ── Ward scope ──────────────────────────────────────────────────────────
  // onlyOwnWard=true  → wardAreaInclusions is the list of EXTRA wards (by
  //   HospitalLocation _id) this person can also work, on top of their own.
  // onlyOwnWard=false → wardExclusions is the list of ward NAMES this
  //   person should never be assigned to; everywhere else is fine.
  // Only one of the two lists applies at a time, matching the flag.
  const [onlyOwnWard,        setOnlyOwnWard]        = useState(false);
  const [wardAreaInclusions, setWardAreaInclusions] = useState([]);
  const [wardExclusions,     setWardExclusions]     = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [register, { isLoading }] = useRegisterMutation();

  const { data: hospitalLocations, isLoading: isLoadingHL, error: errorHL } =
    useGetHospitalLocationsQuery();

  const { userInfo } = useSelector((state) => state.auth);
  const { search }   = useLocation();
  const redirect     = new URLSearchParams(search).get('redirect') || '/';
// In RegisterScreen — add to state
const [registrationToken, setRegistrationToken] = useState('');


  useEffect(() => {
    if (userInfo) navigate(redirect);
  }, [navigate, redirect, userInfo]);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Auto-confirm when all 6 digits entered
  useEffect(() => {
    if (codeSent && !beaumontVerified && beaumontOtp.length === 6) {
      handleConfirmCode();
    }
  }, [beaumontOtp]);

  // If the person's own ward changes, drop it from either list — it doesn't
  // make sense as an "extra" or "excluded" ward once it IS their own ward.
  useEffect(() => {
    setWardAreaInclusions((prev) => prev.filter((id) => id !== specialityID));
    setWardExclusions((prev) => prev.filter((name) => name !== speciality));
  }, [specialityID, speciality]);

  // Ward scope only applies to staff who cover more than one ward — the
  // three Bank/ECT categories always work their own assigned ward only, so
  // the whole section is hidden (and reset) for them.
  const WARD_SCOPE_EXCLUDED_CATEGORIES = ['Bank-HCA', 'Bank-RGN', 'ECT-HCA'];
  // Also requires an own ward to already be selected — "own ward only" and
  // "additional to my own ward" are meaningless before that exists.
  const showWardScope = !WARD_SCOPE_EXCLUDED_CATEGORIES.includes(jobtitle) && !!specialityID;

  useEffect(() => {
    if (!showWardScope) {
      setOnlyOwnWard(false);
      setWardAreaInclusions([]);
      setWardExclusions([]);
    }
  }, [showWardScope]);

  const locationsByDirectorate = (hospitalLocations ?? []).reduce((acc, loc) => {
    if (!acc[loc.directorate]) acc[loc.directorate] = [];
    acc[loc.directorate].push(loc);
    return acc;
  }, {});
  const directorates = Object.keys(locationsByDirectorate).sort();

  // ── Beaumont verification handlers ────────────────────────────────────────
  const handleSendCode = async () => {
    if (!beaumontEmail.trim()) {
      toast.error('Please enter your Beaumont email address');
      return;
    }
    
    if (!beaumontEmail.toLowerCase().endsWith('@beaumont.ie') && !beaumontEmail.toLowerCase().endsWith('@beaumontie.onmicrosoft.com')) {
      toast.error('Only @beaumont.ie or @beaumontie.onmicrosoft.com email addresses are accepted for verification.');
      return;
    }
    try {
      await sendBeaumontCode({ beaumontEmail: beaumontEmail.trim() }).unwrap();
      setCodeSent(true);
      setBeaumontOtp('');
      setResendCooldown(60);
      toast.info('A 6-digit code has been sent to your Beaumont email.', { autoClose: 5000 });
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Could not send code');
    }
  };

  const handleConfirmCode = async () => {
    if (beaumontOtp.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }
    try {
      const result = await confirmBeaumontCode({
        beaumontEmail: beaumontEmail.trim(),
        otp: beaumontOtp,
      }).unwrap();

      setBeaumontVerified(true);
      setRegistrationToken(result.registrationToken);  // ← store it

      toast.success('Beaumont email verified ✓');
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Invalid or expired code');
      setBeaumontOtp('');
    }
  };

  // ── Registration submit ───────────────────────────────────────────────────
  const submitHandler = async (e) => {
    e.preventDefault();

    if (!beaumontVerified) {
      toast.error('Please verify your Beaumont email address before registering');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      await register({
        name: `${firstname} ${lastname}`,
        email,
        password,
        employeenumber,
        firstname,
        lastname,
        jobtitle,
        speciality,
        specialityID,
        directoratedepartment,
        directorateID,
        effectiveDate,
        reviewDate,
        comment,
        salutation,
        openingBalance,
        bringForward,
        onlyOwnWard,
        wardAreaInclusions,
        wardExclusions,
        registrationToken,  // ← backend will verify this
      }).unwrap();

      dispatch(logout());
      dispatch(apiSlice.util.resetApiState()); 

      toast.success('Registration successful. Please log in once your account is approved by Admin.');
      navigate('/login?message=registration-pending');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (isLoadingHL) return <Loader />;

  if (errorHL) {
    const isAuthError =
      errorHL?.status === 401 ||
      errorHL?.data?.message?.toLowerCase().includes('not authorized');
    return (
      <Container fluid className="mt-4">
        <Message variant="danger">
          {isAuthError ? 'Could not load location data.' : errorHL?.data?.message || 'Error loading hospital locations'}
        </Message>
        <Row className="py-3">
          <Col>
            Already have an account?{' '}
            <Link to={redirect ? `/login?redirect=${redirect}` : '/login'}>Login</Link>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid style={{ maxWidth: 1180, padding: '32px 20px 48px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontWeight: 700, marginBottom: 4 }}>Staff Registration</h1>
        <p style={{ color: '#6b7684', fontSize: 15, margin: 0 }}>
          Create your account to request and manage shifts at Beaumont Hospital.
        </p>
      </div>

      {/* ── Beaumont Email Verification Panel ── */}
      <div style={{
        border: beaumontVerified ? '2px solid #4caf50' : '2px solid #1976d2',
        borderRadius: 10,
        padding: 20,
        marginBottom: 24,
        backgroundColor: beaumontVerified ? '#f1f8f1' : '#e3f2fd',
      }}>
        <h5 style={{ marginTop: 0, color: beaumontVerified ? '#2e7d32' : '#1976d2' }}>
          {beaumontVerified
            ? '✓ Beaumont Email Verified'
            : 'Step 1: Verify your Beaumont Hospital email'}
        </h5>

        {beaumontVerified ? (
          <p style={{ margin: 0, color: '#2e7d32', fontWeight: 500 }}>
            Verified as: <strong>{beaumontEmail}</strong>
          </p>
        ) : (
          <>
          <p style={{ color: '#555', fontSize: 14, marginBottom: 12 }}>
            You must verify a <strong>@beaumont.ie</strong> or{' '}
            <strong>@beaumontie.onmicrosoft.com</strong> email address before completing
            registration.
          </p>
          
            <Row className="align-items-end g-2">
              <Col md={6}>
                <Form.Group controlId="beaumontEmail">
                  <Form.Label>Beaumont Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="yourname@beaumont.ie"
                    value={beaumontEmail}
                    onChange={(e) => {
                      setBeaumontEmail(e.target.value);
                      // Reset if they change the email after sending
                      if (codeSent) {
                        setCodeSent(false);
                        setBeaumontOtp('');
                        setBeaumontVerified(false);
                      }
                    }}
                    disabled={beaumontVerified}
                    style={textStyle}
                    isInvalid={beaumontEmail && (!beaumontEmail.toLowerCase().endsWith('@beaumont.ie') && !beaumontEmail.toLowerCase().endsWith('@beaumontie.onmicrosoft.com'))}
                  />
                  <Form.Control.Feedback type="invalid">
                    Must be a @beaumont.ie or @beaumontie.onmicrosoft.com address
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="auto">
                <Button
                  variant="primary"
                  onClick={handleSendCode}
                  disabled={
                    sendingCode ||
                    (!beaumontEmail.toLowerCase().endsWith('@beaumont.ie') && !beaumontEmail.toLowerCase().endsWith('@beaumontie.onmicrosoft.com')) ||
                    resendCooldown > 0
                  }
                >
                  {sendingCode
                    ? 'Sending…'
                    : codeSent && resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : codeSent
                        ? 'Resend Code'
                        : 'Send Verification Code'}
                </Button>
              </Col>
            </Row>

            {codeSent && (
              <div style={{ marginTop: 16 }}>
                <p style={{ color: '#555', fontSize: 14, marginBottom: 4 }}>
                  Enter the 6-digit code sent to <strong>{beaumontEmail}</strong>:
                </p>
                <OTPInput
                  value={beaumontOtp}
                  onChange={setBeaumontOtp}
                  disabled={confirmingCode || beaumontVerified}
                />
                <div style={{ textAlign: 'center', marginTop: 8 }}>
                  <Button
                    variant="success"
                    onClick={handleConfirmCode}
                    disabled={confirmingCode || beaumontOtp.length !== 6}
                  >
                    {confirmingCode ? 'Verifying…' : 'Confirm Code'}
                  </Button>
                </div>
                <p style={{ color: '#888', fontSize: 12, textAlign: 'center', marginTop: 8 }}>
                  Code expires in 10 minutes. Check your spam folder if not received.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Registration form (shown at all times, but submit is blocked until verified) ── */}
      <Form onSubmit={submitHandler}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 14px', borderRadius: 20,
          backgroundColor: '#eef2ff', color: '#3949ab',
          fontSize: 13, fontWeight: 600, marginBottom: 20,
        }}>
          Registering as: Staff Member
        </div>

        <Row className="g-3">
          {/* ── Column 1: Personal details ── */}
          <Col md={5}>
            <div style={cardStyle}>
              <SectionLabel>Your Details</SectionLabel>
              <Row>
                <Col sm={2}>
                  <Form.Group controlId="salutation">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      as="select"
                      value={salutation}
                      onChange={(e) => setSalutation(e.target.value)}
                      style={textStyle}
                    >
                      <option value="">Select</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                      <option value="Dr.">Dr.</option>
                      <option value="Professor">Professor</option>
                      <option value="Mx">Mx</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col sm={5}>
                  <Form.Group controlId="firstname">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="First Name"
                      value={firstname}
                      onChange={(e) => setFirstname(e.target.value)}
                      style={textStyle}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col sm={5}>
                  <Form.Group controlId="lastname">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Last Name"
                      value={lastname}
                      onChange={(e) => setLastname(e.target.value)}
                      style={textStyle}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group controlId="email" className="mt-2">
                <Form.Label>Work / Login Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={textStyle}
                  required
                />
                <Form.Text className="text-muted">
                  This is your login email — it can be any address.
                </Form.Text>
              </Form.Group>

              <Row className="mt-2">
                <Col sm={6}>
                  <Form.Group controlId="employeenumber">
                    <Form.Label>Employee Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Employee Number"
                      value={employeenumber}
                      onChange={(e) => setEmployeenumber(e.target.value)}
                      style={textStyle}
                    />
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group controlId="jobtitle">
                    <Form.Label>Job Title / Category</Form.Label>
                    <Form.Control
                      as="select"
                      value={jobtitle}
                      onChange={(e) => setJobtitle(e.target.value)}
                      style={textStyle}
                    >
                      <option value="">Select Job Title</option>
                      {CATEGORY_OF_STAFF.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>

              {/*
              <Row className="mt-2">
                <Col sm={6}>
                  <Form.Group controlId="effectiveDate">
                    <Form.Label>Effective Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={effectiveDate}
                      readOnly
                      style={{ ...textStyle, cursor: 'not-allowed', opacity: 0.75 }}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Set by administrator after approval
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group controlId="reviewDate">
                    <Form.Label>Review Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={reviewDate}
                      readOnly
                      style={{ ...textStyle, cursor: 'not-allowed', opacity: 0.75 }}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Set to 1 year from today by default
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
                    */}

            </div>

            
          </Col>

          {/* ── Column 2: Hospital location ── */}
          <Col md={4}>
            <div style={cardStyle}>
              <SectionLabel>Ward &amp; Location</SectionLabel>
              <Form.Group controlId="setDirectorate">
                <Form.Label>Directorate</Form.Label>
                <Form.Control
                  as="select"
                  value={directoratedepartment}
                  onChange={(e) => {
                    setDirectoratedepartment(e.target.value);
                    setSpeciality('');
                    setSpecialityID('');
                    setDirectorateID('');
                  }}
                  style={textStyle}
                >
                  <option value="">Select Directorate</option>
                  {directorates.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="wardDepartmentArea" className="mt-3">
                <Form.Label>Ward / Department normally assigned to</Form.Label>
                <Form.Control
                  as="select"
                  value={speciality}
                  onChange={(e) => {
                    const idx         = e.target.selectedIndex;
                    const opts        = e.target.options;
                    const selectedVal = opts[idx].value;
                    const selectedId  = opts[idx].getAttribute('data-id');
                    const selectedDir = opts[idx].getAttribute('data-directorate');
                    setSpeciality(selectedVal);
                    setSpecialityID(selectedId);
                    setDirectorateID(selectedId);
                    if (selectedDir) setDirectoratedepartment(selectedDir);
                  }}
                  disabled={!directoratedepartment}
                  style={textStyle}
                >
                  <option value="">
                    {directoratedepartment ? 'Select Ward / Department' : 'Select a directorate first'}
                  </option>
                  {directoratedepartment &&
                    locationsByDirectorate[directoratedepartment]?.map((loc) => (
                      <option
                        key={loc._id}
                        value={loc.wardDepartmentArea}
                        data-id={loc._id}
                        data-directorate={loc.directorate}
                      >
                        {loc.wardDepartmentArea}
                        {loc.department && ` - ${loc.department}`}
                        {loc.costCentreCode && ` (${loc.costCentreCode})`}
                      </option>
                    ))}
                </Form.Control>
              </Form.Group>

              {specialityID && (
                <div style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 8,
                  backgroundColor: '#fff', border: '1px solid #cfe3f7',
                }}>
                  <small style={{ color: '#3c4655' }}>
                    <strong style={{ color: '#20303e' }}>Selected:</strong><br />
                    Directorate: {directoratedepartment}<br />
                    Ward/Area: {speciality}
                  </small>
                </div>
              )}

              {/* ── Ward scope (not applicable to Bank-HCA / Bank-RGN / ECT-HCA) ── */}
              {showWardScope && (
                <div style={{
                  marginTop: 16, padding: 14, borderRadius: 10,
                  backgroundColor: '#fff', border: '1px solid #cfe3f7',
                }}>
                  <Form.Group controlId="onlyOwnWard">
                    <Form.Check
                      type="checkbox"
                      label="Own Ward Only — I should only be assigned shifts in my own ward (plus any wards I choose to include below)"
                      checked={onlyOwnWard}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setOnlyOwnWard(checked);
                        // Only one list is ever meaningful at a time — clear the other.
                        if (checked) setWardExclusions([]);
                        else setWardAreaInclusions([]);
                      }}
                    />
                  </Form.Group>

                  {onlyOwnWard ? (
                    <Form.Group controlId="wardAreaInclusions" className="mt-2">
                      <Form.Label>Additional Wards You Can Also Work (optional)</Form.Label>
                      <Form.Control
                        as="select"
                        multiple
                        value={wardAreaInclusions}
                        onChange={(e) =>
                          setWardAreaInclusions(Array.from(e.target.selectedOptions, (o) => o.value))
                        }
                        style={textStyle}
                      >
                        {[...(hospitalLocations ?? [])]
                          .filter((loc) => loc._id !== specialityID)
                          .sort((a, b) => (a.wardDepartmentArea || '').localeCompare(b.wardDepartmentArea || ''))
                          .map((loc) => (
                            <option key={loc._id} value={loc._id}>
                              {loc.wardDepartmentArea}
                              {loc.department && ` - ${loc.department}`}
                            </option>
                          ))}
                      </Form.Control>
                      <Form.Text className="text-muted">
                        Hold Ctrl (Windows) or Cmd (Mac) to select more than one. You'll only be
                        assigned shifts in your own ward or the wards selected here.
                      </Form.Text>
                    </Form.Group>
                  ) : (
                    <Form.Group controlId="wardExclusions" className="mt-2">
                      <Form.Label>Wards to Exclude (optional)</Form.Label>
                      <Form.Control
                        as="select"
                        multiple
                        value={wardExclusions}
                        onChange={(e) =>
                          setWardExclusions(Array.from(e.target.selectedOptions, (o) => o.value))
                        }
                        style={textStyle}
                      >
                        {[...new Set((hospitalLocations ?? []).map((loc) => loc.wardDepartmentArea))]
                          .filter((name) => name && name !== speciality)
                          .sort((a, b) => a.localeCompare(b))
                          .map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                      </Form.Control>
                      <Form.Text className="text-muted">
                        Hold Ctrl (Windows) or Cmd (Mac) to select more than one. You'll be able to
                        see and be assigned shifts in any ward except the ones selected here.
                      </Form.Text>
                    </Form.Group>
                  )}
                </div>
              )}
            </div>
          </Col>

          {/* ── Column 3: Password + Comments + balances ── */}
          <Col md={3}>
            <div style={cardStyle}>
              <SectionLabel>Password</SectionLabel>

              <Form.Group controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Set password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={textStyle}
                  required
                />
              </Form.Group>
              <Form.Group controlId="confirmPassword" className="mt-2">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={textStyle}
                  required
                />
              </Form.Group>             
              

              <Form.Group controlId="comment" className="mt-3">
                
                <SectionLabel>Additional Notes</SectionLabel>

                <Form.Label>Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Comments"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  style={textStyle}
                />

              </Form.Group>

              {/*
              <Row className="mt-3">
                <Col sm={6}>
                  <Form.Group controlId="openingBalance">
                    <Form.Label>AL Balance (Hours)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.10"
                      placeholder="0.00"
                      value={openingBalance}
                      onChange={(e) => setOpeningBalance(Number(e.target.value))}
                      style={textStyle}
                    />
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group controlId="bringForward">
                    <Form.Label>Bring Forward (Hours)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.10"
                      placeholder="0.00"
                      value={bringForward}
                      onChange={(e) => setBringForward(Number(e.target.value))}
                      style={textStyle}
                    />
                  </Form.Group>
                
                </Col>
              </Row> */}
              

            </div>
          </Col>
        </Row>

        {/* ── Submit — disabled until beaumont email verified ── */}
        {!beaumontVerified && (
          <div style={{
            marginTop: 20, padding: '12px 16px', borderRadius: 8,
            backgroundColor: '#fdecea', border: '1px solid #f5c6cb',
            color: '#8a2e2e', fontSize: 14, fontWeight: 500,
          }}>
            ⚠ You must verify your Beaumont email (Step 1 above) before you can register.
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          <Button
            disabled={isLoading || !beaumontVerified}
            type="submit"
            variant="primary"
            size="lg"
            style={{ padding: '10px 36px', borderRadius: 8, fontWeight: 600 }}
            title={!beaumontVerified ? 'Verify your Beaumont email first' : ''}
          >
            {isLoading ? 'Registering…' : 'Register'}
          </Button>
        </div>

        {isLoading && <Loader />}
      </Form>

      <Row className="py-3" style={{ borderTop: '1px solid #eceef1', marginTop: 24 }}>
        <Col style={{ color: '#6b7684', fontSize: 14 }}>
          Already have an account?{' '}
          <Link to={redirect ? `/login?redirect=${redirect}` : '/login'}>Login</Link>
        </Col>
      </Row>
    </Container>
  );
};

export default RegisterScreen;